import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';

import { Subscription, Observable, Subject, of } from 'rxjs';

import { Mention } from '../../../../Common/Mentions/models/mention';

import { DropdownOptionModel } from '../../../../Common/Forms/models/dropdown-option.model';

import { MonitoringSourcePickerFieldModel } from '../../models/monitoring-source-picker-field.model';
import { MonitoringSeriesTypeEnum } from '../../enums/monitoring-series-type.enum';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { MentionBadge, mentionBadgesSaved } from '../../../../Common/Mentions/components/MentionCard/mention-card.component';

import { MentionSearchService } from '../../../Core/services/mention-search.service';
import { SearchStatesEnum } from '../../enums/search-states.enum';
import { ScrollPanelComponent } from '../../../../Common/Basic/components/ScrollPanel/scroll-panel.component';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { ImportWarningDialogComponent } from '../../../Briefings/components/ImportWarningDialog/import-warning-dialog.component';
import {
    MonitoringSourcePickerFieldWithFacetComponent
} from '../MonitoringSourcePickerField/monitoring-source-picker-field-with-facet.component';
import { UserPreferencesService } from '../../../../Account/UserPreferences/services/user-preferences.service';
import { PageSizeEnum } from '../../../../api/shared/enums/page-size.enum';
@Component({
    selector: 'agpr-add-mentions-widget',
    templateUrl: './add-mentions-widget.component.html',
    styleUrls: ['./add-mentions-widget.component.scss'],
    providers: [
        MentionSearchService,
        {
            provide: 'PaginatorServiceInterface',
            useExisting: MentionSearchService
        }
    ],
    standalone: false
})
export class AddMentionsWidgetComponent implements OnInit, OnDestroy {

    @Input() id: string = 'addMentionsWidget';
    @Input() showSelectToggle: boolean = true;
    @Input() mentionBadges: MentionBadge[] = mentionBadgesSaved;
    @Input() shouldCheckboxBeDisabled: boolean = false;
    @Input() onlySocial: boolean = false;
    @Input() showSortOrder: boolean = false;

    @Output() searchMentionsChange = new EventEmitter<Mention[]>();
    @Output() selectedMentionsChange = new EventEmitter<Mention[]>();
    @Output() addMentions = new EventEmitter<any>();

    @ViewChild('sourcePicker') sourcePicker: MonitoringSourcePickerFieldWithFacetComponent;
    @ViewChild(ScrollPanelComponent) scrollPanel: ScrollPanelComponent;

    isLoading: boolean = false;
    searchMentions: Mention[] = [];
    selectedMentions: Mention[] = [];
    mentionCount: number = 0;
    allSelected: boolean = false;
    someSelected: boolean = false;

    private pagingValidationSubject: Subject<boolean>;
    private subscriptions: Subscription[] = [];

    readonly messageSeverityEnum = messageSeverityEnum;
    readonly paginationWarningHeading: string = 'Briefings.importTool.warningPagination';
    readonly sourcePickerField: MonitoringSourcePickerFieldModel = new MonitoringSourcePickerFieldModel(
        null,
        'backendDataSource',
        '',
        '',
        {
            dataSourceLabel: 'Monitoring.sourcePickerField.chooseCoverageSource',
            dateRangeLabel: 'Monitoring.sourcePickerField.dateRange',
            mentionCountLocalizationLabel: 'Monitoring.sourcePickerField.briefingsResultFeedbackMessage',
            maxNumberOfMentions: 1000,
            dataSourceNullErrorMessage: 'Monitoring.sourcePickerField.dataSourceRequiredMessage',
            dataSourceOptions: [
                new DropdownOptionModel('Briefings.allSavedCoverage', MonitoringSeriesTypeEnum.allSavedCoverage),
                new DropdownOptionModel('Briefings.folder', MonitoringSeriesTypeEnum.folder),
                new DropdownOptionModel('Monitoring.source.type.topic', MonitoringSeriesTypeEnum.topic),
                new DropdownOptionModel('Monitoring.source.type.subscription', MonitoringSeriesTypeEnum.subscription),
                new DropdownOptionModel('Monitoring.source.type.tag', MonitoringSeriesTypeEnum.tag),
                new DropdownOptionModel('Monitoring.source.type.tagGroup', MonitoringSeriesTypeEnum.tagGroup),
                new DropdownOptionModel('Monitoring.source.type.sourceGroup', MonitoringSeriesTypeEnum.sourceGroup),
                new DropdownOptionModel('Reports.series.type.socialTopic', MonitoringSeriesTypeEnum.socialTopic),
                new DropdownOptionModel('Reports.series.type.theme', MonitoringSeriesTypeEnum.theme),
                new DropdownOptionModel('Reports.series.type.aiAgent', MonitoringSeriesTypeEnum.aiAgent)
            ],
            horizontalOrientation: true,
            showResultsFeedback: false,
            showLoadingFeedback: false,
            showFilters: true,
            showTooManyMentionsError: false
        },
    );

    constructor(
        public coreDialogService: CoreDialogService,
        private searchService: MentionSearchService,
        private userPreferencesService: UserPreferencesService
    ) {}

    ngOnInit() {
        this.subscriptions.push(
            this.searchService.getItemCount().subscribe(
                (mentionCount) => {
                    if (!mentionCount) {
                        this.mentionCount = 0;
                        return;
                    }
                    this.mentionCount = mentionCount;
                }
            ),
            this.searchService.getSearchState().subscribe(
                (state) => {
                    this.isLoading = state.type === SearchStatesEnum.loading;
                }
            ),
            this.searchService.getMentions().subscribe(
                (mentions) => {
                    this.searchMentions = mentions || [];
                    this.searchMentionsChange.emit(this.searchMentions.slice());

                    this.selectNone();
                    if (this.scrollPanel) {
                        this.scrollPanel.scrollTo(0, 0, false);
                    }
                }
            ),
            this.userPreferencesService.getUserAppPreferences().subscribe(
                (userAppPrefs) => {
                    const pageSize = userAppPrefs.monitoringPrefs.briefingsPrefs?.defaultDisplaySettings?.pageSize || PageSizeEnum.twentyFive;
                    this.searchService.setPageSize(pageSize);
                }
            )
        );
        this.searchService.registerPagingValidator(this.pagingValidator.bind(this));
    }

    /**
     * Selects all mentions if none are selected, or de-selects all selected
     */
    toggleSelect() {
        if (this.selectedMentions.length < this.searchMentions.length) {
            this.selectAll();
        } else {
            this.selectNone();
        }
    }

    /**
     * Selects all mentions on the page
     *
     * @returns void
     */
    selectAll(): void {
        this.selectedMentions = this.searchMentions.slice();
        this.selectedMentionsChanged();
    }

    /**
     * Deselects all mentions on the page
     *
     * @returns void
     */
    selectNone(): void {
        this.selectedMentions = [];
        this.selectedMentionsChanged();
    }

    selectedMentionsChanged(): void {
        this.selectedMentionsChange.emit(this.selectedMentions.slice());
        this.updateCheckboxState();
    }

    updateCheckboxState() {
        if (this.selectedMentions.length > 0) {
            if (this.selectedMentions.length === this.searchMentions.length) {
                this.allSelected = true;
                this.someSelected = false;
            } else {
                this.allSelected = false;
                this.someSelected = true;
            }
        } else {
            this.allSelected = false;
            this.someSelected = false;
        }
    }

    /**
     * Validates that paging can be performed at this time
     * @NOTE if mentions are selected, then let the user verify/decide what to do
     */
    pagingValidator(): Observable<boolean> {
        if (this.selectedMentions.length > 0) {
            this.pagingValidationSubject = new Subject<boolean>();
            this.showWarning(this.paginationWarningHeading, () => {
                this.pagingValidationSubject.next(true);
                this.pagingValidationSubject = null;
            });
            return this.pagingValidationSubject;
        }
        return of(true);
    }

    /**
     * Shows the selected mentions warning dialog with the proper heading and action to be performed
     */
    showWarning(heading: string, action: Function): void {
        this.coreDialogService.open(
            ImportWarningDialogComponent,
            {
                inputs: {
                    header: heading
                },
                outputs: {
                    dialogClose: ({confirm, mentions}) => {
                        if (confirm) {
                            if (mentions) {
                                this.addMentions.emit();
                            }
                            action();
                        }
                        if (this.pagingValidationSubject) {
                            this.pagingValidationSubject.next(false);
                            this.pagingValidationSubject = null;
                        }
                    }
                }
            }
        );
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
